const MVCObject = require("../mvcobject").MVCObject;
const TemplateRenderer = require("../templaterenderer").TemplateRenderer;
const $ = require("../jquery_with_plugins");

/**
 * InputState
 * @param {Element} element
 * @param {String} [geolocDisplayValue]
 * @constructor
 * @private
 */
const InputState = function (element, geolocDisplayValue) {
    /**
     * @private
     */
    this.element = element;
    /**
     * @private
     */
    this.location = null;
    /**
     * @private
     */
    this.store = null;

    this._geolocDisplayValue = geolocDisplayValue;
};

InputState.prototype._lngLatAsString = (lng, lat) =>
    `${Number(lng.toFixed(5)).toString()},${Number(lat.toFixed(5)).toString()}`;

InputState.prototype.getDisplayValue = function () {
    if (this.location !== null) {
        if (this._geolocDisplayValue) {
            return this._geolocDisplayValue;
        }
        return this._lngLatAsString(this.location.lng, this.location.lat);
    }
    if (this.store !== null) {
        return this.store.properties.name;
    }
    return this.element.val();
};

InputState.prototype.getValue = function () {
    if (this.location !== null) {
        return this._lngLatAsString(this.location.lng, this.location.lat);
    }
    if (this.store !== null) {
        const coordinates = this.store.geometry.coordinates;
        return `${coordinates[0]},${coordinates[1]}`;
    }
    return this.element.val();
};

InputState.prototype.setValue = function (newValue) {
    this.location = null;
    this.store = null;
    this.element.val(newValue);
};

/**
 * @typedef {Object} OdInputOptions
 * @property {String} [geolocText] Text to display in input field after geolocation
 */

/**
 * @typedef {Object} OriginDestination
 * @property {String} origin
 * @property {String} destination
 */

/**
 * Take the template for an Origin Destination Panel and link with an originDestination object
 * @param {String} originDestinationPanelTemplate template of the origin destination panel
 * @param {OdInputOptions} [odInputOptions]
 * @constructor
 * @extends woosmap.utils.MVCObject
 * @memberOf woosmap.ui
 */
const OriginDestinationInput = function (originDestinationPanelTemplate, odInputOptions) {
    /**
     * The text to display when a Location is used as origin or destination.
     * @type {String}
     * @private
     */
    this.geolocText = null;

    if (odInputOptions !== undefined) {
        this.geolocText = odInputOptions.geolocText;
    }

    /**
     * @type {woosmap.TemplateRenderer}
     * @private
     */
    this._renderer = new TemplateRenderer(originDestinationPanelTemplate);
    this.$originDestinationPanel = $("<div>");
    this.$originDestinationPanel.append($(this._renderer.render()));

    /**
     *
     * @type {OriginDestination}
     */
    this.originDestination = null;

    /**
     * Flag to decide where to put the store name.
     * @type {boolean}
     */
    this.storeIsDestination = true;

    /**
     * The selected store.
     * This property is bindable.
     * @type {Store}
     */
    this.selectedStore = null;

    /**
     * The location to use to compute the directions.
     * @type {LatLngLiteral}
     */
    this.location = null;

    this.$originDestinationPanel.find(".woosmap-direction-submit").click(
        function () {
            this.updateOriginDestination();
        }.bind(this),
    );

    this.$originDestinationPanel.find(".woosmap-toggle-origin-destination").click(this.switch.bind(this));

    this.$originDestinationPanel.find(".woosmap-destination").keydown(this.onKeyUp.bind(this));
    this.$originDestinationPanel.find(".woosmap-origin").keydown(this.onKeyUp.bind(this));
    this.$destination = this.$originDestinationPanel.find(".woosmap-destination");
    this.$origin = this.$originDestinationPanel.find(".woosmap-origin");

    /**
     * Origin input state.
     * @type {InputState}
     * @private
     */
    this._originState = new InputState(this.$origin, this.geolocText);

    /**
     * Destination input state.
     * @type {InputState}
     * @private
     */
    this._destinationState = new InputState(this.$destination, this.geolocText);

    function registerInputHandler(element, getStateFunc) {
        element.on("input", () => {
            const state = getStateFunc();
            state.location = null;
            state.store = null;
        });
    }

    registerInputHandler(
        this.$origin,
        function () {
            return this._originState;
        }.bind(this),
    );
    registerInputHandler(
        this.$destination,
        function () {
            return this._destinationState;
        }.bind(this),
    );
};

OriginDestinationInput.prototype = new MVCObject();

/**
 * switches the two inputs values.
 * @private
 */
OriginDestinationInput.prototype.switch = function () {
    this.storeIsDestination = !this.storeIsDestination;

    let tmpOrigin = this._originState;
    this._originState = this._destinationState;
    this._destinationState = tmpOrigin;

    this._originState.element = this.$origin;
    this._destinationState.element = this.$destination;

    tmpOrigin = this.$origin.val();
    this.$origin.val(this.$destination.val());
    this.$destination.val(tmpOrigin);

    this.updateOriginDestination();
};

/**
 * Return true if the selectedStore has a name equal to the name in parameters
 * @param {String} name the name which will be compared to selectedStore.name
 */
OriginDestinationInput.prototype.isSelectedStoreName = function (name) {
    return this.get("selectedStore") && this.get("selectedStore").properties.name === name;
};

/**
 * Set the originDestination object {origin: ***, destination: ***} with value containing in woosmap-origin and woosmap-destination input
 * If one name is the same that selectedStore's name then store position is set instead of the name
 */
OriginDestinationInput.prototype.updateOriginDestination = function () {
    const originValue = this._originState.getValue();
    const destinationValue = this._destinationState.getValue();

    const originDisplayValue = this._originState.getDisplayValue();
    const destinationDisplayValue = this._destinationState.getDisplayValue();

    this.$origin.val(originDisplayValue || "");
    this.$destination.val(destinationDisplayValue || "");

    if (originValue && destinationValue) {
        this.set("originDestination", {
            origin: originValue,
            destination: destinationValue,
        });
    }
};

/**
 * Set the value of woosmap-origin input
 * @param {String} newOriginValue the value to set
 */
OriginDestinationInput.prototype.setOriginValue = function (newOriginValue) {
    this._originState.setValue(newOriginValue);
};

/**
 * Set the value of woosmap-destination input
 * @param {String} newDestinationValue the value to set
 */
OriginDestinationInput.prototype.setDestinationValue = function (newDestinationValue) {
    this._destinationState.setValue(newDestinationValue);
};

/**
 * Get the value of woosmap-origin input
 * return selectedStore position is value is the same tha store name
 * @param {boolean} [useLatLng]  if param is set to true and the selectedStore is the origin store
 * then return lat,lng instead of input value
 */
OriginDestinationInput.prototype.getOriginValue = function (useLatLng) {
    if (useLatLng) {
        return this._originState.getValue();
    }
    return this._originState.getDisplayValue();
};

/**
 * Get the value of woosmap-destination input
 * return selectedStore position is value is the same tha store name
 * @param {boolean} [useLatLng] if param is set to true and the selectedStore is the destination store
 * then return lat,lng instead of input value
 */
OriginDestinationInput.prototype.getDestinationValue = function (useLatLng) {
    if (useLatLng) {
        return this._destinationState.getValue();
    }
    return this._destinationState.getDisplayValue();
};

/**
 * Method called when keyUp on origin or destination input
 * @param {Event} event
 */
OriginDestinationInput.prototype.onKeyUp = function (event) {
    if (event.keyCode === 13) {
        this.updateOriginDestination();
        event.preventDefault();
        return false;
    }
};

/**
 * Method called when selectedStore changed
 */
OriginDestinationInput.prototype.selectedStore_changed = function () {
    const selectedStore = this.get("selectedStore");
    if (selectedStore) {
        if (this.storeIsDestination) {
            this._destinationState.store = selectedStore;
        } else {
            this._originState.store = selectedStore;
        }
        this.updateOriginDestination();
    }
};

/**
 * Method called when location changed
 */
OriginDestinationInput.prototype.location_changed = function () {
    const location = this.get("location");
    if (this.storeIsDestination) {
        this._originState.location = location;
    } else {
        this._destinationState.location = location;
    }
    this.updateOriginDestination();
};

/**
 * Return the container instantiated with origin-destination panel
 */
OriginDestinationInput.prototype.getODContainer = function () {
    return this.$originDestinationPanel;
};

module.exports = {
    OriginDestinationInput,
};
